import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  Paper
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Email as EmailIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import api from '../services/api';

/** Una sola petición por token (Strict Mode / doble efecto; el token solo sirve una vez en el servidor) */
const verifyEmailPromiseByToken = new Map<string, Promise<{ success: boolean; message?: string; data?: any }>>();

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Token de verificación no encontrado');
      setLoading(false);
      return;
    }

    let cancelled = false;

    const run = async () => {
      try {
        setLoading(true);

        if (!verifyEmailPromiseByToken.has(token)) {
          verifyEmailPromiseByToken.set(token, api.verifyEmail(token));
        }
        const response = await verifyEmailPromiseByToken.get(token)!;

        if (cancelled) return;

        if (response.success) {
          setSuccess(true);
          setUserData(response.data?.user);
        } else {
          verifyEmailPromiseByToken.delete(token);
          setError(response.message || 'Error verificando email');
        }
      } catch (err: any) {
        if (cancelled) return;
        verifyEmailPromiseByToken.delete(token);
        console.error('Error verificando email:', err);
        const msg =
          err?.response?.data?.message ||
          (err?.message && err.message !== 'Token expirado' ? err.message : null) ||
          'Error del servidor';
        setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleGoToLogin = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h5" gutterBottom>
            Verificando tu email...
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Por favor espera mientras verificamos tu cuenta.
          </Typography>
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 3 }} />
          <Typography variant="h5" gutterBottom color="error">
            Error de Verificación
          </Typography>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            El enlace de verificación puede haber expirado o ser inválido.
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            startIcon={<ArrowForwardIcon />}
          >
            Ir al Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (success) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          <Typography variant="h4" gutterBottom color="success.main">
            ¡Email Verificado!
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            ¡Hola {userData?.firstName} {userData?.lastName}!
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Tu cuenta ha sido verificada exitosamente. Ya puedes acceder al portal de formación en ciberseguridad.
          </Typography>

          <Alert severity="success" sx={{ mb: 4 }}>
            <Typography variant="body2">
              <strong>¡Bienvenido a Vigilantium Learning!</strong><br />
              Tu email <strong>{userData?.email}</strong> ha sido verificado correctamente.
            </Typography>
          </Alert>

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              ¿Qué puedes hacer ahora?
            </Typography>
            <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto' }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                📚 Acceder a cursos de ciberseguridad
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                🎯 Realizar evaluaciones de conocimiento
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                📊 Ver tu progreso en el dashboard
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                🛡️ Participar en simulaciones de seguridad
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            size="large"
            onClick={handleGoToLogin}
            startIcon={<ArrowForwardIcon />}
            sx={{ 
              px: 4, 
              py: 1.5,
              fontSize: '1.1rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
              }
            }}
          >
            Acceder al Portal
          </Button>
        </Paper>
      </Container>
    );
  }

  return null;
};

export default VerifyEmail;
